import React, { useCallback, useMemo, useState } from 'react';

import { Navigate, useParams } from 'react-router-dom';

import useFeatureFlags from 'api/useFeatureFlags';
import { EFeatureFlag, EPopupSubType, OfferType } from 'constants/enums';
import { CircleFadingPlus } from 'lucide-react';
import { OfferDesignTypes } from 'views/OffersUI/OffersUIForm/types';

import { OfferPageLayout } from '../../components/OfferPageLayout';
import { useOfferDesignOptions } from '../../hooks/useOfferDesignOptions';
import { useOfferTable } from '../../hooks/useOfferTable';
import { CheatsDialog } from '../../RollingOffers/RollingOffersTable/components/CheatsDialog';

import { RewardCalendarFilters } from './components/RewardCalendarFilters';
import { useRewardCalendarColumns } from './hooks/useRewardCalendarColumns';
import { useRewardCalendarFilters } from './hooks/useRewardCalendarFilters';

const RewardCalendarTable = (): React.ReactElement | null => {
  const { publisherId } = useParams();
  const { featureFlags, isLoading } = useFeatureFlags({ publisherId });

  if (isLoading) {
    return null;
  }

  if (!featureFlags?.[EFeatureFlag.DASHBOARD_REWARD_CALENDAR]) {
    return <Navigate to="../popups" replace />;
  }

  return <RewardCalendarTableContent />;
};

const RewardCalendarTableContent = (): React.ReactElement => {
  const { publisherId } = useParams();

  const { featureFlags } = useFeatureFlags({ publisherId });
  const hasFeatureFlagCheats =
    !!featureFlags?.[EFeatureFlag.DASHBOARD_ROLLING_CHEATS];

  const [cheatsOfferId, setCheatsOfferId] = useState<string | null>(null);
  const onOpenCheats = useCallback((offerId: string) => {
    setCheatsOfferId(offerId);
  }, []);
  const handleCloseCheats = useCallback(() => {
    setCheatsOfferId(null);
  }, []);

  const [externalIdFilter, setExternalIdFilter] = useState('');
  const [nameFilter, setNameFilter] = useState('');

  const {
    getOffers,
    currentPage,
    setCurrentPage,
    rowsPerPage,
    sorting,
    updateSorting,
    permissions,
    canManageOffers,
    canUserEdit,
    actions,
    selectedOffer,
    isDeleteDialogOpen,
    showLimitedEditModal,
    handleDeleteOffer,
    handleCreate,
    handleCloseLimitedEditModal,
    handleCloseDeleteDialog,
    offers,
    processOffers,
    updateRewardCalendarCollectableDay
  } = useOfferTable({
    offerType: OfferType.POPUP,
    offerSubType: EPopupSubType.REWARD_CALENDAR,
    offerLabel: 'Reward Calendar',
    needsPricings: false
  });

  const handleCheatsSubmit = useCallback<
    React.ComponentProps<typeof CheatsDialog>['onSubmit']
  >(
    ({ indexCollected, playerId, offerId }, options) => {
      updateRewardCalendarCollectableDay.mutate(
        { targetIndex: indexCollected, playerId, offerId },
        options
      );
    },
    [updateRewardCalendarCollectableDay]
  );

  const { offerDesignOptions } = useOfferDesignOptions({
    publisherId: publisherId || '',
    offerDesignType: OfferDesignTypes.PopUp
  });

  const offerDesignFilterOptions = useMemo(
    () =>
      offerDesignOptions.map((opt) => ({
        value: opt.value,
        label: opt.label
      })),
    [offerDesignOptions]
  );

  const {
    statusFilter,
    offerDesignFilter,
    createdAtFilter,
    hasAnyFilter,
    filteredOffers,
    handleStatusChange,
    handleOfferDesignChange,
    handleCreatedAtChange,
    handleClearFilters
  } = useRewardCalendarFilters({
    offers,
    externalIdFilter,
    setExternalIdFilter,
    nameFilter,
    setNameFilter
  });

  const { columns } = useRewardCalendarColumns({
    sorting,
    updateSorting,
    isLoading: getOffers.isLoading,
    actions,
    permissions,
    hasFeatureFlagCheats,
    onOpenCheats
  });

  const processed = useMemo(
    () => processOffers(filteredOffers),
    [filteredOffers, processOffers]
  );

  const customFilters = (
    <RewardCalendarFilters
      externalIdFilter={externalIdFilter}
      onExternalIdChange={setExternalIdFilter}
      nameFilter={nameFilter}
      onNameChange={setNameFilter}
      statusFilter={statusFilter}
      onStatusChange={handleStatusChange}
      offerDesignFilter={offerDesignFilter}
      offerDesignOptions={offerDesignFilterOptions}
      onOfferDesignChange={handleOfferDesignChange}
      createdAtFilter={createdAtFilter}
      onCreatedAtChange={handleCreatedAtChange}
      hasAnyFilter={hasAnyFilter}
      onClearFilters={handleClearFilters}
    />
  );

  return (
    <OfferPageLayout
      title="Reward Calendars"
      buttonText={processed.buttonText}
      onCreateClick={handleCreate}
      canUserEdit={canUserEdit}
      canManageOffers={canManageOffers}
      showFilters={offers.length > 0}
      customFilters={customFilters}
      offers={offers}
      displayData={processed.sorted}
      columns={columns}
      totalCount={processed.totalCount}
      totalPages={processed.totalPages}
      rowsPerPage={rowsPerPage}
      currentPage={currentPage}
      onPageChange={setCurrentPage}
      isLoading={getOffers.isLoading}
      emptyStateTitle="Add your first Reward Calendar"
      emptyStateDescription="Encourage daily engagement by offering players rewards they can collect each day. Set up your calendar to define rewards, progression, and behavior."
      createButtonPrefix="Reward Calendar"
      emptyStateIcon={
        <CircleFadingPlus className="text-base-mutedForeground h-6 w-6" />
      }
      bodyDataTestId="dashRewardCalendarList"
      offerType={OfferType.POPUP}
      offerDesignType={OfferDesignTypes.PopUp}
      selectedOffer={selectedOffer}
      showLimitedEditModal={showLimitedEditModal}
      onCloseLimitedEditModal={handleCloseLimitedEditModal}
      isDeleteDialogOpen={isDeleteDialogOpen}
      onCloseDeleteDialog={handleCloseDeleteDialog}
      onConfirmDelete={handleDeleteOffer}
      deleteModalTitle="Delete Reward Calendar"
      deleteModalDescription="Are you sure you want to delete this reward calendar?"
    >
      <CheatsDialog
        cheatsOfferId={cheatsOfferId}
        onSubmit={handleCheatsSubmit}
        onClose={handleCloseCheats}
      />
    </OfferPageLayout>
  );
};

export { RewardCalendarTable };
